import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import fallbackImage from "../assets/images/service.jpg";
import { useGetBlogQuery, useGetBlogsQuery } from "../store/api/blogsApi";
import BlogCard from "../components/BlogCard";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const { data: blogRes, isLoading, isError } = useGetBlogQuery(slug);
  const { data: allRes } = useGetBlogsQuery();

  const blog = blogRes?.data || null;
  const related = (allRes?.data || [])
    .filter((b) => (b.slug || b.id) !== slug && b.category === blog?.category)
    .slice(0, 3);

  const cover = blog?.coverImage || fallbackImage;

  const formattedDate = blog?.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    : null;

  if (isLoading) {
    return (
      <div className="bg-bg min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="font-manrope-regular text-sm text-secondary">Loading article…</p>
        </div>
      </div>
    );
  }

  if (isError || !blog) {
    return (
      <div className="bg-bg min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-manrope-bold text-lg text-secondary">Article not found.</p>
        <Link to="/blog" className="font-manrope-semibold text-sm text-primary hover:opacity-80">
          ← Back to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-bg">
      {/* Hero cover */}
      <div className="relative w-full h-[300px] md:h-[440px] overflow-hidden">
        <img src={cover} alt={blog.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Breadcrumb */}
        <div className="absolute top-6 left-0 right-0 container mx-auto px-4">
          <div className="flex flex-wrap items-center gap-1 text-white/70 font-manrope-regular text-sm">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-white line-clamp-1">{blog.title}</span>
          </div>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8 md:pb-12">
          {blog.category && (
            <span className="inline-block bg-primary text-white font-manrope-semibold text-xs px-3 py-1 rounded-full mb-3">
              {blog.category}
            </span>
          )}
          <h1 className="font-manrope-bold text-[24px] md:text-[38px] text-white leading-tight max-w-3xl">
            {blog.title}
          </h1>
        </div>
      </div>

      {/* Article body */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Main content */}
          <article className="w-full lg:flex-1 max-w-3xl">
            {/* Meta bar */}
            <div className="flex flex-wrap items-center gap-4 pb-6 mb-8 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-manrope-bold text-xs">
                    {(blog.author || "C")[0].toUpperCase()}
                  </span>
                </div>
                <span className="font-manrope-semibold text-sm text-black">{blog.author || "Contentour Bonaire"}</span>
              </div>
              {formattedDate && (
                <span className="font-manrope-regular text-sm text-secondary">{formattedDate}</span>
              )}
              {blog.readTime && (
                <span className="font-manrope-regular text-sm text-secondary flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {blog.readTime} min read
                </span>
              )}
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <p className="font-manrope-semibold text-lg text-black leading-relaxed mb-6">
                {blog.excerpt}
              </p>
            )}

            {/* Body content */}
            <div className="font-manrope-regular text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
              {blog.content}
            </div>

            {/* Back link */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <button
                onClick={() => navigate("/blog")}
                className="flex items-center gap-2 font-manrope-semibold text-sm text-primary hover:opacity-80 transition-opacity"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back to all articles
              </button>
            </div>
          </article>

          {/* Sidebar — related posts */}
          {related.length > 0 && (
            <aside className="w-full lg:w-72 flex flex-col gap-4 flex-shrink-0">
              <h3 className="font-manrope-bold text-base text-black">Related articles</h3>
              {related.map((b) => (
                <Link
                  key={b.id || b._id}
                  to={`/blog/${b.slug || b.id}`}
                  className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow group"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <img
                      src={b.coverImage || fallbackImage}
                      alt={b.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <p className="font-manrope-semibold text-sm text-black leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                      {b.title}
                    </p>
                    {b.readTime && (
                      <span className="font-manrope-regular text-xs text-secondary">{b.readTime} min read</span>
                    )}
                  </div>
                </Link>
              ))}
            </aside>
          )}
        </div>
      </div>

      {/* More from blog */}
      {(allRes?.data || []).filter((b) => (b.slug || b.id) !== slug).length > 0 && (
        <div className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4 flex flex-col gap-8">
            <div className="flex items-center justify-between">
              <h2 className="font-manrope-bold text-[22px] md:text-[28px] text-black">More articles</h2>
              <Link to="/blog" className="font-manrope-medium text-sm text-primary hover:opacity-80 transition-opacity">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(allRes?.data || [])
                .filter((b) => (b.slug || b.id) !== slug)
                .slice(0, 3)
                .map((b) => (
                  <BlogCard key={b.id || b._id} blog={b} />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
